import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { AddCircle, Delete } from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const weekDays = [
  { label: "Domingo", value: 0 },
  { label: "Lunes", value: 1 },
  { label: "Martes", value: 2 },
  { label: "Miércoles", value: 3 },
  { label: "Jueves", value: 4 },
  { label: "Viernes", value: 5 },
  { label: "Sábado", value: 6 },
];

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  scheduleRow: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  formControl: {
    width: "100%",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  topActions: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    justifyContent: "flex-end",
  },
}));

const DoctorSchedulesForm = forwardRef(function DoctorSchedulesForm(
  {
    doctorId = null,
    initialSchedules = [], 
    onChange = null,
  },
  ref
) {
  const classes = useStyles();

  const [loading, setLoading] = useState(Boolean(doctorId));
  const [saving, setSaving] = useState(false);

  const [schedules, setSchedules] = useState(initialSchedules || []);
  const [originalSchedules, setOriginalSchedules] = useState(initialSchedules || []);

  const modifiedIdsRef = useRef(new Set());

  useEffect(() => {
    if (!doctorId) return;

    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/doctorSchedules", { params: { doctorId } });
        const sorted = (data || []).sort(
          (a, b) => a.dayOfWeek - b.dayOfWeek || (a.startTime || "").localeCompare(b.startTime || "")
        );
        if (!mounted) return;
        setSchedules(sorted);
        setOriginalSchedules(sorted);
      } catch (err) {
        toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetch();

    return () => {
      mounted = false;
    };
  }, [doctorId]);

  useImperativeHandle(ref, () => ({
    saveToServer: async (targetDoctorId) => {
      if (!targetDoctorId) throw new Error("doctorId requerido para guardar en servidor");
      await _saveSchedulesInternal(targetDoctorId);
    },
    getSchedules: () => schedules,
    reload: async () => {
      if (!doctorId) return;
      try {
        setLoading(true);
        const { data } = await api.get("/doctorSchedules", { params: { doctorId } });
        const sorted = (data || []).sort(
          (a, b) =>
            a.dayOfWeek - b.dayOfWeek ||
            (a.startTime || "").localeCompare(b.startTime || "")
        );
        setSchedules(sorted);
        setOriginalSchedules(sorted);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
  }));

  const markModified = (idx) => {
    const s = schedules[idx];
    if (s && s.id) {
      modifiedIdsRef.current.add(s.id);
    }
    setSchedules((prev) => [...prev]);
    if (!doctorId && typeof onChange === "function") onChange(schedules);
  };

  const updateSchedule = (idx, field, value) => {
    const newSchedules = schedules.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
    setSchedules(newSchedules);
    markModified(idx);
  };

  const addDay = () => {
    const item = {
      dayOfWeek: 1,
      startTime: "09:00",
      endTime: "17:00",
      requiresAppointment: true,
      appointmentDuration: schedules[0]?.appointmentDuration || 30,
    };
    setSchedules((prev) => [...prev, item]);
    if (!doctorId && typeof onChange === "function") onChange([...schedules, item]);
  };

  const removeDay = async (idx) => {
    const scheduleToRemove = schedules[idx];
    const newSchedules = schedules.filter((_, i) => i !== idx);
    setSchedules(newSchedules);
    if (!doctorId) {
      setOriginalSchedules((prev) => prev.filter((s) => s !== scheduleToRemove));
      if (typeof onChange === "function") onChange(newSchedules);
      return;
    }

    if (scheduleToRemove && scheduleToRemove.id) {
      try {
        await api.delete('/doctorSchedules/' + scheduleToRemove.id);
        setOriginalSchedules(prev => prev.filter(s => s.id !== scheduleToRemove.id));
        toast.success('Horario eliminado correctamente');
      } catch (err) {
        toastError(err);
      }
    }
  };

  const _saveSchedulesInternal = async (targetDoctorId = doctorId) => {
    if (!targetDoctorId) {
      throw new Error("Doctor id requerido para guardar schedules");
    }

    setSaving(true);
    try {
      const origById = {};
      (originalSchedules || []).forEach((o) => {
        if (o.id) origById[o.id] = o;
      });

      const currentIds = new Set((schedules || []).map((s) => s.id).filter(Boolean));
      const deletes = (originalSchedules || [])
        .filter(o => o.id && !currentIds.has(o.id))
        .map(o => api.delete('/doctorSchedules/' + o.id));

      const upserts = (schedules || []).map((s) => {
        const payload = {
          doctorId: Number(targetDoctorId),
          dayOfWeek: s.dayOfWeek,
          startTime: s.startTime,
          endTime: s.endTime,
          requiresAppointment: !!s.requiresAppointment,
          appointmentDuration: Number(s.appointmentDuration || 0),
        };

        if (!s.id) {
          return api.post("/doctorSchedules", payload);
        } else {
          const o = origById[s.id];
          const changed =
            !o ||
            Number(o.dayOfWeek) !== Number(s.dayOfWeek) ||
            (o.startTime || "") !== (s.startTime || "") ||
            (o.endTime || "") !== (s.endTime || "") ||
            !!o.requiresAppointment !== !!s.requiresAppointment ||
            Number(o.appointmentDuration) !== Number(s.appointmentDuration);

          return changed ? api.put(`/doctorSchedules/${s.id}`, payload) : null;
        }
      }).filter(Boolean);

      const allRequests = [...deletes, ...upserts];
      if (allRequests.length === 0) {
        setSaving(false);
        return;
      }

      await Promise.all(allRequests);

      if (targetDoctorId) {
        const { data } = await api.get("/doctorSchedules", { params: { doctorId: targetDoctorId } });
        const sorted = (data || []).sort(
          (a, b) => a.dayOfWeek - b.dayOfWeek || (a.startTime || "").localeCompare(b.startTime || "")
        );
        setSchedules(sorted);
        setOriginalSchedules(sorted);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDurationChange = (value) => {
    setSchedules(prev => prev.map(s => ({ ...s, appointmentDuration: value })));
    if (!doctorId && typeof onChange === "function") onChange(schedules);
  };

  return (
    <div className={classes.container}>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12}>
          <TextField
            label="Duración de la consulta (minutos)"
            type="number"
            value={schedules[0]?.appointmentDuration || 30}
            onChange={(e) => handleDurationChange(Number(e.target.value))}
            fullWidth
            variant="outlined"
            className={classes.textField}
          />
        </Grid>
      </Grid>

      {schedules.map((schedule, idx) => (
        <Grid container spacing={2} className={classes.scheduleRow} key={idx}>
          <Grid item xs={12} sm={3}>
            <FormControl variant="outlined" className={classes.formControl} size="small">
              <InputLabel>Día</InputLabel>
              <Select
                value={schedule.dayOfWeek}
                onChange={(e) => updateSchedule(idx, "dayOfWeek", e.target.value)}
                label="Día"
              >
                {weekDays.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Hora inicio"
              type="time"
              value={schedule.startTime}
              onChange={(e) => updateSchedule(idx, "startTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.textField}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Hora fin"
              type="time"
              value={schedule.endTime}
              onChange={(e) => updateSchedule(idx, "endTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.textField}
            />
          </Grid>

          <Grid item xs={12} sm={3} style={{ display: "flex", alignItems: "center"}}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!schedule.requiresAppointment}
                  onChange={(e) => updateSchedule(idx, "requiresAppointment", e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Cita"
            />
            <IconButton
              onClick={() => removeDay(idx)}
              className={classes.deleteButton}
            >
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button
        startIcon={<AddCircle />}
        onClick={addDay}
        variant="contained"
        fullWidth
        className={classes.addButton}
      >
        Agregar día y horario
      </Button>
    </div>
  );
});

export default DoctorSchedulesForm;
